import * as Dialog from '@studio/design-system/dialog';
import { useComposedRefs } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import { forwardRef, useRef } from 'react';
import { Context } from './context.js';
import { Scaffold } from './scaffold.jsx';
import type { Props as ProviderProps } from './store/index.js';
import { Provider } from './store/index.js';
import type { Props } from './types.js';
import { useWorkflow } from './wiring.js';

const Workflow = forwardRef<Dialog.Api, Omit<Props, keyof ProviderProps>>(
  (props, forwardedRef): ReactElement => {
    const apiRef = useRef<Dialog.Api>(null);
    const wiring = useWorkflow(apiRef);

    return (
      <Dialog.Root ref={useComposedRefs(apiRef, forwardedRef)} {...props}>
        <Dialog.Overlay className='z-[100]'>
          <Dialog.Sheet>
            <Context.Provider value={wiring}>
              <Scaffold />
            </Context.Provider>
          </Dialog.Sheet>
        </Dialog.Overlay>
      </Dialog.Root>
    );
  }
);

/** Video `<Settings>` component. */
// `defaultPanel` + `defaultTab` are the deep-link surface — chip click
// → useSettings → here. Both are forwarded into `<Provider>` so the
// initial store state opens at the deep-link target instead of the
// default `Panel.IrisConnection` / `monitoring` sub-tab.
//
// `exactOptionalPropertyTypes` is enabled in this tsconfig — passing
// `undefined` to an optional prop is rejected. We conditionally spread
// the deep-link props so they're omitted entirely when not provided.
export const Settings = forwardRef<Dialog.Api, Props>(
  (
    { agentId, device, status, defaultPanel, defaultTab, roomId, onDeviceCommitted, ...props },
    forwardedRef
  ): ReactElement => {
    // `exactOptionalPropertyTypes` is on — spread optional props only when
    // defined so `undefined` is never passed to an optional prop. `roomId`
    // joins this set (Multi-Add Phase 2): it threads the dialog's room into
    // the store so the Authentication tab can resolve it for a transitory
    // Iris placeholder. Per `reference-rules-in-comments` (ai.knowledge/global).
    const deepLinkProps = {
      ...(defaultPanel !== undefined && { defaultPanel }),
      ...(defaultTab !== undefined && { defaultTab }),
      ...(roomId !== undefined && { roomId }),
      ...(onDeviceCommitted !== undefined && { onDeviceCommitted })
    };
    return (
      <Provider
        agentId={agentId}
        device={device}
        key={device.id}
        status={status}
        {...deepLinkProps}
      >
        <Workflow ref={forwardedRef} {...props} />
      </Provider>
    );
  }
);
